import { History } from 'history';
import { PropsWithChildren, createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Router, useHistory } from 'react-router';
import { Box, Flex } from 'reflexbox';

import { Modal } from '@treams/treams-ui/common/Modal';
import { Popover } from '@treams/treams-ui/common/Popover';

export const ModalContext = createContext({ isModal: false, close: () => {} });

export function useModalLink(url: string, modal?: boolean) {
	const history = useHistory();

	if (modal === false) {
		return url;
	}
	const params = new URLSearchParams(history.location.search);
	params.set('modal', url);

	return history.location.pathname + '?' + params.toString();
}

function isGoalViewModal(url: string) {
	const path = url.split('?')[0];
	const reserved = new Set(['new', 'organization', 'collective', 'competencies']);
	const match = path.match(/^\/(?:users\/[^/]+\/)?goals\/([^/]+)$/);

	return !!match && !reserved.has(match[1]);
}

export default function ModalNavigation(props: PropsWithChildren<{ url: string }>) {
	const { t } = useTranslation();
	const history = useHistory();

	const listeners = useRef<Record<string, (location: any) => void>>({}).current;
	const containerRef = useRef<HTMLDivElement>(null);

	const visible = !!props.url;

	const lastURL = useRef(props.url);

	if (props.url) {
		lastURL.current = props.url;
	}

	const url = lastURL.current;
	const title = isGoalViewModal(url) ? t('goals.show.modal.title') : undefined;

	const [zoom, setZoom] = useState(false);

	const close = useCallback(() => {
		const location = history.location;
		const params = new URLSearchParams(location.search);
		params.delete('modal');
		location.search = params.toString();
		history.push(location);
	}, [history]);

	useEffect(() => {
		for (const listener of Object.values(listeners)) {
			listener(new URL('https://treams.com' + url));
		}

		setZoom(false);
	}, [props.url]);

	const fake = useRef({
		...history,
		push(location: unknown) {
			if (location === null) {
				console.error('Location in ModalNavigation is null, this should not happen.');

				return history.push(
					history.createHref({
						...history.location,
						search: '',
					}),
				);
			}

			const setModal = (modalUrl: string) => {
				const params = new URLSearchParams(history.location.search);
				params.set('modal', modalUrl);

				return history.push(
					history.createHref({
						...history.location,
						search: `?${params.toString()}`,
					}),
				);
			};

			if (typeof location === 'string') {
				return setModal(location);
			}

			if (typeof location === 'object') {
				const current = new URL('https://treams.com' + lastURL.current);
				const nextPathname =
					'pathname' in location && typeof location.pathname === 'string'
						? location.pathname
						: current.pathname;
				const rawSearch =
					'search' in location && typeof location.search === 'string'
						? location.search
						: 'pathname' in location
							? ''
							: current.search;
				const nextSearch = rawSearch
					? rawSearch.startsWith('?')
						? rawSearch
						: `?${rawSearch}`
					: '';

				return setModal(`${nextPathname}${nextSearch}`);
			}
		},
		listen(listener: any) {
			const id = Math.random();
			listeners[id] = listener;

			return () => {
				delete listeners[id];
			};
		},
		location: new URL('https://treams.com' + url),
	}).current as unknown as History;

	(fake as any).location = new URL('https://treams.com' + url) as any;

	/**
	 * Scrolls the modal container to the top when the modal is opened.
	 * This is to prevent the modal from being opened with the user scrolled down,
	 * which can hide certain inputs.
	 */
	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}, []);

	return (
		<Popover visible={visible} backdrop center zoom={zoom}>
			{lastURL.current && (
				<Flex flexDirection="column" width={800} maxHeight="calc(90vh)" maxWidth="calc(90vw)">
					<Modal.Header
						title={title}
						onFullScreen={() => {
							setZoom(true);
							history.push(props.url);
						}}
						onClose={close}
					/>
					<Box overflowY="scroll" ref={containerRef}>
						<Router key={url.split('?')[0]} history={fake}>
							<ModalContext.Provider value={{ isModal: true, close }}>
								{props.children}
							</ModalContext.Provider>
						</Router>
					</Box>
				</Flex>
			)}
		</Popover>
	);
}
